import {
    ActionType,
    gaasApiServiceClientFactory,
    TransactionDetails,
    useApplicationContext,
} from '@gaas/gaas-react-component-library';
import config from '../config';
import {userInfo} from 'src/utils/sessionStorage';
import routes from 'src/routes';
import {History} from 'history';
import {useState} from 'react';
import {isErrorRedirect} from '../services/httpClient/responseInterceptor';

export const CORRELATION_ID_KEY = 'CORRELATION_ID';

/**
 * Custom hook to do "StartTransaction" process
 */
function useStartTransaction(history: History) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {dispatch, globalState} = useApplicationContext();
    const gaasAppApiService = gaasApiServiceClientFactory(config);

    const isInitialised = () => !!globalState.transactionDetails?.correlationId;

    const clear = () => {
        sessionStorage.removeItem(CORRELATION_ID_KEY)
        // perhaps better if GaaS can add a new ActionType for this, eg. CLEAR_TRANSACTION without payload required
        dispatch({
            // use UPDATE_OTHER_DATA as per mentioned in gaas react component lib
            // this will completely replace any section defined in the other data
            type: ActionType.UPDATE_OTHER_DATA,
            payload: {
                profileConnectInfo: null,
                userInfo: null,
                transactionDetails: null,
                supportingDocuments: { documents: [], errors: {} },
            },
        });
    }

    const initialise = async () => {
        setIsLoading(true);
        try {

            const {correlationId, customerData} = await gaasAppApiService.startTransaction();
            const userInfoObj = userInfo();

            if (!correlationId || !userInfoObj) {
                console.error(`Required data is not available; correlationId: ${correlationId}`);
                history.push(routes.errorPage.path);
                return;
            }
            if (!customerData.firstName || !customerData.familyName) {
                console.error('FirstName and/or FamilyName is empty')
                history.push(routes.preferredNameErrorPage.path);
                return;
            }

            const transactionDetails: TransactionDetails = {
                correlationId: correlationId,
            };

            if (config?.appConfig?.enableDebug) {
                console.log('correlationId: ', correlationId);
                console.log('userInfoObj: ', userInfoObj);
                console.log('customerDataObj: ', customerData);
            }

            sessionStorage.setItem(CORRELATION_ID_KEY, correlationId);

            dispatch({
                type: ActionType.INITIALISE_TRANSACTION,
                payload: {
                    profileConnectInfo: customerData,
                    userInfo: userInfoObj,
                    transactionDetails: transactionDetails,
                },
            });
        } catch (e: any) {
            console.error('startTransaction error occurred', e)
            if(isErrorRedirect(e)) {
                history.push(e.redirect);
            } else {
                history.push('/error/500');
            }
        }
        setIsLoading(false);
    }

    return {
        isLoading,
        /**
         * Function to check if Transaction has been initialised.
         * Based on Transaction correlationId existence
         */
        isInitialised,

        /**
         * Function to initialise transaction.
         * Should be called on every start transaction, eg. first page of application form
         * When it is not initialised yet, check 'isInitialised' above.
         *
         * It will redirect to error page when correlationId or userInfo is falsy
         */
        initialise,

        /**
         * Function to clear transaction.
         * Should be called on every finish transaction, eg. after submission success
         */
        clear,
    }
}

export default useStartTransaction;
